import React from 'react';
import './MaskedIcon.css';

/**
 * Тон иконки — чем ее красить. SURFACE берет цвет у поверхности
 * (--icon-color): на светлых страницах он черный, темные панели
 * переопределяют его у себя. CONTROLS — цвет элементов управления
 * (--controls-color), одинаковый везде.
 */
export const MASKED_ICON_TONES = Object.freeze({
  SURFACE: 'surface',
  CONTROLS: 'controls',
});

const TONE_CLASS_NAMES = {
  [MASKED_ICON_TONES.CONTROLS]: 'masked-icon_controls',
};

/**
 * Иконка, перекрашенная маской: сама картинка служит только силуэтом, а цвет
 * задается фоном элемента. Нужна там, где один и тот же файл показывается и на
 * светлой подложке (страницы правил, модалки), и на темной (футер партии):
 * дубли файлов заводить не нужно — цвет берется из CSS-переменной
 * --icon-color, которую темная панель переопределяет у себя (см.
 * MaskedIcon.css и Footer.css).
 *
 * Годится только для ОДНОЦВЕТНЫХ силуэтов: маска заливает всю непрозрачную
 * часть одним цветом, поэтому многоцветные иконки (например «unit d6
 * friend.png» — черный контур со светлой заливкой, или «building_damage.png» с
 * оранжевым пламенем) так перекрашивать нельзя, от них останется пятно.
 *
 * Размер задает место использования — тот же класс, что раньше висел на <img>
 * (например policy-description-icon). Внутри лежит скрытая картинка-мерка: она
 * сохраняет исходные пропорции, поэтому неквадратные иконки (cell.png,
 * size rect.png) не нужно описывать отдельно.
 *
 * @param {string} src - путь к картинке-силуэту. Именно рантайм-путь
 *   (/img/icons/...): маска задается инлайн-стилем, а не CSS-файлом, где
 *   пришлось бы писать /public/img/... (css-loader резолвит такие пути сборкой
 *   и вшивает картинку в бандл base64). Пробелы в именах файлов внутри url()
 *   обязаны быть в кавычках — иначе CSS-токен ломается
 * @param {string} [alt] - подпись для скринридера; без нее иконка считается
 *   декоративной и прячется от них (пустой aria-label хуже, чем его отсутствие)
 * @param {string} [title] - всплывающая подсказка
 * @param {string} [tone] - см. MASKED_ICON_TONES
 * @param {string} [color] - явный цвет, если тона мало
 * @param {string} [className]
 * @param {Object} [style]
 */
export function MaskedIcon({
  src,
  alt,
  title,
  tone = MASKED_ICON_TONES.SURFACE,
  color,
  className = '',
  style,
  ...rest
}) {
  const toneClassName = TONE_CLASS_NAMES[tone] || '';
  const labelProps = alt
    ? {role: 'img', 'aria-label': alt}
    : {'aria-hidden': 'true'};

  return (
    <span
      className={`masked-icon ${toneClassName} ${className}`}
      style={{
        ...style,
        WebkitMaskImage: `url("${src}")`,
        maskImage: `url("${src}")`,
        ...(color ? {backgroundColor: color} : null),
      }}
      title={title}
      {...labelProps}
      {...rest}
    >
      <img className={'masked-icon_sizer'} src={src} alt={''} aria-hidden={'true'}/>
    </span>
  );
}

export default MaskedIcon;
